import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useControllableState } from '@radix-ui/react-use-controllable-state'

import { useObject } from 'data/hooks/objects'
import { makeAllFields } from 'features/views/ListView/utils'
import { useRecordManagerContext } from 'features/views/RecordManager/useRecordManagerContext'

import useDeepEqualsMemoValue from 'v2/ui/utils/useDeepEqualsMemoValue'

import { AttributeContext, AttributeContextValue } from './hooks/useAttributeContext'
import { useIsFieldEditable } from './hooks/useIsFieldEditable'
import { EditableAttribute } from './types'

const LOADING_SLOW_THRESHOLD_TIMEOUT = 2000

type InlineEditingAttributeContextProviderProps = {
    editingFields?: string[]
    toggleEditingField?: (fieldApiName: string, enabled: boolean) => void
    field: EditableAttribute
    isLoading?: boolean
    overrideIsEditable?: boolean
}

export const InlineEditingAttributeContextProvider: React.FC<
    InlineEditingAttributeContextProviderProps
> = ({
    children,
    editingFields: providedEditingFields,
    toggleEditingField: providedToggleEditingField,
    field,
    isLoading = false,
    overrideIsEditable,
}) => {
    const [editingFields = [], setEditingFields] = useControllableState({
        prop: providedEditingFields,
    })

    const toggleEditingField = useCallback(
        (fieldApiName: string, enabled: boolean) => {
            setEditingFields((prev = []) => {
                if (enabled) return [...prev, fieldApiName]

                return prev.filter((f) => f !== fieldApiName)
            })

            providedToggleEditingField?.(fieldApiName, enabled)
        },
        [setEditingFields, providedToggleEditingField]
    )

    const fieldApiName = field.field.api_name
    const isEditingValue = editingFields.includes(fieldApiName)

    const {
        record,
        mutateAttribute,
        replaceAttribute,
        clearAttribute,
        saveChanges,
        discardChanges: discardRecordChanges,
    } = useRecordManagerContext()
    const attrValue = record?.[fieldApiName] ?? undefined

    const { object } = useObject(record?._object_id)
    const objectMemo = useDeepEqualsMemoValue(object)

    const fields = useDeepEqualsMemoValue(makeAllFields(objectMemo))
    const fieldsRef = useRef(fields)
    fieldsRef.current = fields

    const fieldRef = useRef(field)
    fieldRef.current = field

    const toggleEditingValueMode = useCallback(
        (enabled: boolean) => {
            toggleEditingField(fieldApiName, enabled)
        },
        [fieldApiName, toggleEditingField]
    )

    const label = field.label || field.field.label
    const labelRef = useRef(label)
    labelRef.current = label

    const [isSaving, setIsSaving] = useState(false)
    const saveValue = useCallback(async () => {
        setIsSaving(true)

        // Wait for the next frame to ensure the value has been updated.
        return new Promise<void>(async (resolve) => {
            requestAnimationFrame(async () => {
                await saveChanges({
                    successToast: {
                        resolveFieldLabel: (fieldApiName) => {
                            if (fieldRef.current.field.api_name === fieldApiName) {
                                return labelRef.current
                            }

                            return undefined
                        },
                    },
                })

                toggleEditingValueMode(false)
                setIsSaving(false)
                resolve()
            })
        })
    }, [saveChanges, toggleEditingValueMode])

    const [isSavingSlow, setIsSavingSlow] = useState(false)
    useEffect(() => {
        let timer: number

        if (isSaving) {
            timer = window.setTimeout(() => {
                setIsSavingSlow(true)
            }, LOADING_SLOW_THRESHOLD_TIMEOUT)
        } else {
            setIsSavingSlow(false)
        }

        return () => {
            window.clearTimeout(timer)
        }
    }, [isSaving])

    const clearValue = useCallback(async () => {
        clearAttribute(fieldApiName)

        return saveValue()
    }, [clearAttribute, fieldApiName, saveValue])

    const discardChanges = useCallback(() => {
        discardRecordChanges()
        toggleEditingValueMode(false)
    }, [discardRecordChanges, toggleEditingValueMode])

    const getIsAttributeEditable = useIsFieldEditable()
    let isEditable = getIsAttributeEditable(field.field) && !isLoading && !isSavingSlow
    if (overrideIsEditable !== undefined) {
        isEditable = overrideIsEditable
    }
    const isEditableRef = useRef(isEditable)
    isEditableRef.current = isEditable
    const requestEditValueMode = useCallback(() => {
        if (!isEditableRef.current) return

        toggleEditingValueMode(true)
    }, [toggleEditingValueMode])

    const mutateValue: AttributeContextValue<any>['mutateValue'] = useCallback(
        (tr) => {
            const currentField = fieldRef.current
            const fields = fieldsRef.current

            const field = fields.find((f) => f.api_name === currentField.field.api_name)
            if (!field) return

            mutateAttribute(currentField.field.api_name, tr)
            if (field.is_primary) {
                mutateAttribute('_primary', tr)
            }
        },
        [mutateAttribute]
    )

    const replaceValue: AttributeContextValue<any>['replaceValue'] = useCallback(
        (value) => {
            const currentField = fieldRef.current
            const fields = fieldsRef.current

            const field = fields.find((f) => f.api_name === currentField.field.api_name)
            if (!field) return

            replaceAttribute(currentField.field.api_name, value)
            if (field.is_primary) {
                replaceAttribute('_primary', value)
            }
        },
        [replaceAttribute]
    )

    const value = useMemo(
        () => ({
            isEditable,
            isEditingValue,
            value: attrValue,
            toggleEditingValueMode,
            field,
            label,
            mutateValue,
            replaceValue,
            saveValue,
            isSavingSlow,
            clearValue,
            discardChanges,
            requestEditValueMode,
            object: objectMemo,
        }),
        [
            isEditable,
            isEditingValue,
            attrValue,
            toggleEditingValueMode,
            field,
            label,
            mutateValue,
            replaceValue,
            saveValue,
            isSavingSlow,
            clearValue,
            discardChanges,
            requestEditValueMode,
            objectMemo,
        ]
    )

    return <AttributeContext.Provider value={value}>{children}</AttributeContext.Provider>
}
